import type { HistoricalRideImportResult, HistoricalRideImportRow, HistoricalRideImportSummary } from "../historicalRideImport";
import type { HistoricalFitImportPerformanceDiagnostics } from "../workers/historicalFitWorkerProtocol";
import type { HistoricalFitWorkerDiagnostics } from "../workers/historicalFitWorkerClient";
import { classifyImportTimeAttribution } from "../workers/workerHandoffProfiling";
import { useEffect, useMemo, useState } from "react";

type HistoricalImportRuntimeDiagnostics = {
  fileName: string | null;
  fileSizeBytes: number | null;
  workerStartedAt: string | null;
  currentStage: string | null;
  stageStartedAt: string | null;
  lastWorkerMessageAt: string | null;
  workerState: string | null;
  workerActive: boolean;
  cancellationAvailable: boolean;
  performanceDiagnostics?: HistoricalFitImportPerformanceDiagnostics | null;
};

type HistoricalRideImportPanelProps = {
  importResult: HistoricalRideImportResult | null;
  isImporting: boolean;
  progressPercent: number;
  progressStage?: string | null;
  progressStatus?: string | null;
  workerDiagnostics?: HistoricalFitWorkerDiagnostics | null;
  runtimeDiagnostics?: HistoricalImportRuntimeDiagnostics | null;
  onSelectFiles: (files: FileList | null) => void;
  onCancelImport: () => void;
};

const LONG_PARSING_THRESHOLD_SECONDS = 30;

function formatStageLabel(stage: string | null | undefined): string {
  if (!stage) return "queued";
  return stage.replace(/_/g, " ");
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes) || bytes < 0) return "--";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

function toElapsedSeconds(startIso: string | null, nowMs: number): number | null {
  if (!startIso) return null;
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((nowMs - start) / 1000));
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "--";
  const rounded = Math.max(0, Math.round(seconds));
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remain = rounded % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remain}s`;
  return `${remain}s`;
}

function formatDurationMs(milliseconds: number | null): string {
  if (milliseconds === null || !Number.isFinite(milliseconds)) return "--";
  const safeMs = Math.max(0, Math.round(milliseconds));
  if (safeMs >= 1000) return `${(safeMs / 1000).toFixed(1)}s`;
  return `${safeMs}ms`;
}

function formatDistanceMeters(distanceMeters: number | null): string {
  if (distanceMeters === null || !Number.isFinite(distanceMeters)) return "--";
  const kilometers = distanceMeters / 1000;
  if (kilometers >= 10) return `${kilometers.toFixed(1)} km`;
  return `${kilometers.toFixed(2)} km`;
}

function formatThroughput(throughput: number | null): string {
  if (throughput === null || !Number.isFinite(throughput)) return "--";
  return `${throughput.toFixed(2)} MB/s`;
}

function summaryOrEmpty(summary: HistoricalRideImportSummary | null): HistoricalRideImportSummary {
  return summary ?? {
    filesScanned: 0,
    filesRecognized: 0,
    filesRejected: 0,
    duplicatesDetected: 0,
    importsCompleted: 0,
    importsFailed: 0,
    ridesCreated: 0,
    canonicalMatches: 0,
    provenanceMerges: 0,
    failedParses: 0,
    cancelled: 0,
    persistenceFailures: 0
  };
}

function rowStatusBadge(row: HistoricalRideImportRow): string {
  if (row.failureType === "cancelled") return "Cancelled";
  if (row.persistenceStatus === "saved" && row.duplicateStatus === "canonical_match") return "Canonical match";
  if (row.persistenceStatus === "saved" && row.duplicateStatus === "duplicate") return "Duplicate";
  if (row.persistenceStatus === "saved") return "Imported";
  if (row.parseStatus === "rejected") return "Rejected";
  if (row.parseStatus === "failed" || row.persistenceStatus === "failed") return "Failed";
  if (row.parseStatus === "parsed") return "Parsed";
  return "Pending";
}

export function HistoricalRideImportPanel({
  importResult,
  isImporting,
  progressPercent,
  progressStage,
  progressStatus,
  workerDiagnostics,
  runtimeDiagnostics,
  onSelectFiles = () => undefined,
  onCancelImport = () => undefined
}: HistoricalRideImportPanelProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!isImporting) {
      return;
    }
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isImporting]);

  const rows = importResult?.rows ?? [];
  const summary = summaryOrEmpty(importResult?.summary ?? null);
  const stageElapsedSeconds = useMemo(() => toElapsedSeconds(runtimeDiagnostics?.stageStartedAt ?? null, nowMs), [runtimeDiagnostics?.stageStartedAt, nowMs]);
  const importElapsedSeconds = useMemo(() => toElapsedSeconds(runtimeDiagnostics?.workerStartedAt ?? null, nowMs), [runtimeDiagnostics?.workerStartedAt, nowMs]);
  const longParsing = (runtimeDiagnostics?.currentStage ?? progressStage) === "parsing"
    && stageElapsedSeconds !== null
    && stageElapsedSeconds >= LONG_PARSING_THRESHOLD_SECONDS;
  const performanceDiagnostics = runtimeDiagnostics?.performanceDiagnostics ?? null;
  const handoffProfile = workerDiagnostics?.handoffProfile ?? null;
  const timeAttribution = useMemo(() => {
    if (!performanceDiagnostics) {
      return null;
    }
    return classifyImportTimeAttribution({
      totalElapsedMs: performanceDiagnostics.totalElapsedMs,
      mainThreadToWorkerPostMs: performanceDiagnostics.stages.mainThreadToWorkerPostMs,
      workerReceiveLatencyMs: performanceDiagnostics.stages.workerReceiveLatencyMs,
      workerResultTransferMs: performanceDiagnostics.stages.workerResultTransferMs,
      fitDecodeMs: performanceDiagnostics.workerStages.binaryDecodeMs ?? performanceDiagnostics.stages.fitDecodeMs,
      recordExtractionMs: performanceDiagnostics.workerStages.recordExtractionMs ?? null,
      normalizationMs: performanceDiagnostics.workerStages.recordNormalizationMs ?? performanceDiagnostics.stages.normalizationMs,
      analyticsMs: performanceDiagnostics.stages.analyticsMs,
      persistenceMs: performanceDiagnostics.stages.persistenceMs
    });
  }, [performanceDiagnostics]);

  return (
    <section className="historical-import-panel" aria-labelledby="historical-import-title">
      <div className="activity-history-header">
        <div>
          <p className="eyebrow">HISTORICAL IMPORT</p>
          <h2 id="historical-import-title">Import Ride History</h2>
          <p className="command-note">Batch import historical Garmin FIT and TrainingPeaks FIT.gz exports into local ride history.</p>
        </div>
        <span className="coach-summary-badge">{summary.importsCompleted} imported</span>
      </div>

      <div className="activity-intake-actions">
        <label className="ride-analysis-button primary-action" role="button">
          Select Historical Ride Files
          <input
            type="file"
            multiple
            accept=".fit,.fit.gz"
            disabled={isImporting}
            onChange={event => {
              onSelectFiles(event.target.files);
              event.target.value = "";
            }}
          />
        </label>
        <button type="button" className="ride-analysis-button secondary-action" onClick={onCancelImport} disabled={!isImporting}>Cancel Import</button>
      </div>

      {isImporting ? (
        <div className="upload-progress" aria-live="polite">
          <div className="upload-progress-heading">
            <strong>Historical import in progress</strong>
            <span>Coarse progress {progressPercent}%</span>
          </div>
          <progress value={progressPercent} max={100} aria-label="Historical import progress" />
          <p className="command-note" style={{ marginTop: 8 }}>
            Stage: {formatStageLabel(progressStage)} · {progressStatus ?? "Processing historical FIT"}
          </p>
          <p className="command-note" style={{ marginTop: 6 }}>
            {runtimeDiagnostics?.currentStage === "parsing" ? "Parsing FIT data..." : "Processing historical FIT stage..."}
            {" "}
            {runtimeDiagnostics?.currentStage === "parsing" ? "The FIT decoder does not expose granular progress." : ""}
          </p>
          <p className="command-note" style={{ marginTop: 4 }}>
            Elapsed in stage: {stageElapsedSeconds === null ? "--" : `${stageElapsedSeconds}s`} · Worker {runtimeDiagnostics?.workerActive ? "active" : "idle"}
          </p>
          {longParsing ? (
            <p className="command-note" style={{ marginTop: 4 }}>
              Parsing is taking longer than usual. Worker is still active. You may continue waiting or cancel.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="command-grid metrics-3" style={{ marginTop: 12 }}>
        <article className="summary-item"><span>Selected</span><strong>{summary.filesScanned}</strong></article>
        <article className="summary-item"><span>Recognized</span><strong>{summary.filesRecognized}</strong></article>
        <article className="summary-item"><span>Rejected</span><strong>{summary.filesRejected}</strong></article>
        <article className="summary-item"><span>Duplicates</span><strong>{summary.duplicatesDetected}</strong></article>
        <article className="summary-item"><span>Imported</span><strong>{summary.importsCompleted}</strong></article>
        <article className="summary-item"><span>Failed</span><strong>{summary.importsFailed}</strong></article>
      </div>

      {rows.length === 0 ? (
        <p className="command-note" style={{ marginTop: 12 }}>No historical files selected yet.</p>
      ) : (
        <div className="historical-import-table-wrap" style={{ marginTop: 12 }}>
          <table className="historical-import-table">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Date</th>
                <th>Sport</th>
                <th>Duration</th>
                <th>Distance</th>
                <th>Parse</th>
                <th>Duplicate</th>
                <th>Persistence</th>
                <th>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={`${row.fileName}:${row.fileSizeBytes}`}>
                  <td>{row.fileName}</td>
                  <td>{row.detectedRideDate ? new Date(row.detectedRideDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "--"}</td>
                  <td>{row.sport ?? "--"}</td>
                  <td>{formatDuration(row.durationSeconds)}</td>
                  <td>{formatDistanceMeters(row.distanceMeters)}</td>
                  <td>{row.parseStatus}</td>
                  <td>{row.duplicateStatus}</td>
                  <td>{row.persistenceStatus}</td>
                  <td>{rowStatusBadge(row)}</td>
                  <td>{row.message ?? "--"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details className="upload-diagnostics" style={{ marginTop: 12 }}>
        <summary>Historical import diagnostics</summary>
        <dl>
          <div><dt>Files scanned</dt><dd>{summary.filesScanned}</dd></div>
          <div><dt>Files accepted</dt><dd>{summary.filesRecognized}</dd></div>
          <div><dt>Files rejected</dt><dd>{summary.filesRejected}</dd></div>
          <div><dt>Duplicates detected</dt><dd>{summary.duplicatesDetected}</dd></div>
          <div><dt>Rides created</dt><dd>{summary.ridesCreated}</dd></div>
          <div><dt>Canonical matches</dt><dd>{summary.canonicalMatches}</dd></div>
          <div><dt>Provenance merges</dt><dd>{summary.provenanceMerges}</dd></div>
          <div><dt>Failed parses</dt><dd>{summary.failedParses}</dd></div>
          <div><dt>Cancelled</dt><dd>{summary.cancelled}</dd></div>
          <div><dt>Persistence failures</dt><dd>{summary.persistenceFailures}</dd></div>
          <div><dt>Persistence result</dt><dd>{summary.cancelled > 0 && summary.importsCompleted === 0 ? "cancelled / no_write" : summary.persistenceFailures > 0 ? "persistence_failed" : summary.importsFailed > 0 ? "partial" : summary.importsCompleted > 0 ? "success" : "none"}</dd></div>
          <div><dt>Worker stage</dt><dd>{workerDiagnostics?.stage ?? progressStage ?? "queued"}</dd></div>
          <div><dt>Worker state</dt><dd>{workerDiagnostics?.workerState ?? "idle"}</dd></div>
          <div><dt>Last worker progress</dt><dd>{workerDiagnostics?.lastProgressAt ?? "--"}</dd></div>
          <div><dt>Current file</dt><dd>{runtimeDiagnostics?.fileName ?? "--"}</dd></div>
          <div><dt>File size</dt><dd>{formatBytes(runtimeDiagnostics?.fileSizeBytes ?? null)}</dd></div>
          <div><dt>Worker started</dt><dd>{runtimeDiagnostics?.workerStartedAt ?? "--"}</dd></div>
          <div><dt>Stage started</dt><dd>{runtimeDiagnostics?.stageStartedAt ?? "--"}</dd></div>
          <div><dt>Import elapsed</dt><dd>{importElapsedSeconds === null ? "--" : `${importElapsedSeconds}s`}</dd></div>
          <div><dt>Stage elapsed</dt><dd>{stageElapsedSeconds === null ? "--" : `${stageElapsedSeconds}s`}</dd></div>
          <div><dt>Worker active</dt><dd>{runtimeDiagnostics?.workerActive ? "yes" : "no"}</dd></div>
          <div><dt>Cancel available</dt><dd>{runtimeDiagnostics?.cancellationAvailable ? "yes" : "no"}</dd></div>
          <div><dt>Long parsing notice</dt><dd>{longParsing ? "shown" : "not shown"}</dd></div>
        </dl>
      </details>

      {performanceDiagnostics ? (
        <details className="upload-diagnostics" style={{ marginTop: 12 }}>
          <summary>Performance diagnostics</summary>
          <dl>
            <div><dt>Total elapsed</dt><dd>{formatDurationMs(performanceDiagnostics.totalElapsedMs)}</dd></div>
            <div><dt>Worker elapsed</dt><dd>{formatDurationMs(performanceDiagnostics.workerElapsedMs)}</dd></div>
            <div><dt>Main-thread post-worker</dt><dd>{formatDurationMs(performanceDiagnostics.mainThreadPostWorkerElapsedMs)}</dd></div>
            <div><dt>Classification</dt><dd>{performanceDiagnostics.classification}</dd></div>
            <div><dt>File size</dt><dd>{formatBytes(performanceDiagnostics.fileBytes)}</dd></div>
            <div><dt>Throughput</dt><dd>{formatThroughput(performanceDiagnostics.throughputMbPerSecond)}</dd></div>
            <div><dt>Records/sec</dt><dd>{performanceDiagnostics.recordsPerSecond === null ? "--" : performanceDiagnostics.recordsPerSecond.toFixed(1)}</dd></div>
            <div><dt>Largest stage</dt><dd>{performanceDiagnostics.largestStageLabel ?? "--"}</dd></div>
            <div><dt>Largest stage %</dt><dd>{performanceDiagnostics.largestStagePercent === null ? "--" : `${performanceDiagnostics.largestStagePercent}%`}</dd></div>
            <div><dt>Time attribution</dt><dd>{timeAttribution ? `${timeAttribution.dominant} · transport ${formatDurationMs(timeAttribution.transportMs)} · processing ${formatDurationMs(timeAttribution.processingMs)} · persistence ${formatDurationMs(timeAttribution.persistenceMs)}` : "--"}</dd></div>
            <div><dt>Main-thread read</dt><dd>{formatDurationMs(performanceDiagnostics.mainThreadFileReadMs)}</dd></div>
            <div><dt>Main-thread → worker</dt><dd>{formatDurationMs(performanceDiagnostics.mainThreadToWorkerPostMs)}</dd></div>
            <div><dt>Worker receive latency</dt><dd>{formatDurationMs(performanceDiagnostics.workerReceiveLatencyMs)}</dd></div>
            <div><dt>FIT decode</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.binaryDecodeMs ?? performanceDiagnostics.stages.fitDecodeMs)}</dd></div>
            <div><dt>Summary selection</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.summarySelectionMs ?? null)}</dd></div>
            <div><dt>Summary field extraction</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.summaryFieldExtractionMs ?? null)}</dd></div>
            <div><dt>Record extraction</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.recordExtractionMs ?? null)}</dd></div>
            <div><dt>Record normalization</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.recordNormalizationMs ?? performanceDiagnostics.stages.normalizationMs)}</dd></div>
            <div><dt>Summary reconciliation</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.summaryReconciliationMs ?? null)}</dd></div>
            <div><dt>Parser total</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.parserTotalMs ?? null)}</dd></div>
            <div><dt>Upload pipeline total</dt><dd>{formatDurationMs(performanceDiagnostics.workerStages.uploadPipelineMs ?? null)}</dd></div>
            <div><dt>Analytics</dt><dd>{formatDurationMs(performanceDiagnostics.stages.analyticsMs)}</dd></div>
            <div><dt>Worker result transfer</dt><dd>{formatDurationMs(performanceDiagnostics.workerResultTransferMs)}</dd></div>
            <div><dt>Persistence</dt><dd>{formatDurationMs(performanceDiagnostics.stages.persistenceMs)}</dd></div>
            <div><dt>History refresh</dt><dd>{formatDurationMs(performanceDiagnostics.stages.historyRefreshMs)}</dd></div>
            <div><dt>Traversed nodes</dt><dd>{performanceDiagnostics.counts.parsedStructureNodeCount ?? "--"}</dd></div>
            <div><dt>FIT message count</dt><dd>{performanceDiagnostics.counts.fitMessageCount ?? "--"}</dd></div>
            <div><dt>FIT record count</dt><dd>{performanceDiagnostics.counts.fitRecordCount ?? "--"}</dd></div>
            <div><dt>Session / lap / event messages</dt><dd>{`${performanceDiagnostics.counts.sessionCount ?? "--"} / ${performanceDiagnostics.counts.lapCount ?? "--"} / ${performanceDiagnostics.counts.eventCount ?? "--"}`}</dd></div>
            <div><dt>Device / developer messages</dt><dd>{`${performanceDiagnostics.counts.deviceInfoCount ?? "--"} / ${performanceDiagnostics.counts.developerFieldCount ?? "--"}`}</dd></div>
            <div><dt>Ignored message groups</dt><dd>{performanceDiagnostics.counts.ignoredMessageGroupCount ?? "--"}</dd></div>
            <div><dt>Record candidates mapped</dt><dd>{performanceDiagnostics.counts.recordCandidatesMapped ?? "--"}</dd></div>
            <div><dt>Record candidates excluded by path</dt><dd>{performanceDiagnostics.counts.recordCandidatesExcludedByPath ?? "--"}</dd></div>
            <div><dt>Duplicate candidates skipped</dt><dd>{performanceDiagnostics.counts.recordCandidatesSkippedAsDuplicate ?? "--"}</dd></div>
            <div><dt>Normalized samples</dt><dd>{performanceDiagnostics.counts.normalizedSamplesProduced ?? "--"}</dd></div>
            <div><dt>Analysis metrics</dt><dd>{performanceDiagnostics.counts.analysisMetricCount ?? "--"}</dd></div>
          </dl>
        </details>
      ) : null}

      <details className="upload-diagnostics" style={{ marginTop: 12 }} data-testid="worker-handoff-profiling">
        <summary>Worker handoff timings</summary>
        <p className="command-note">
          Cross-context latencies use epoch-aligned high-resolution clocks.
          {handoffProfile?.clockSkewSuspected ? " Warning: main-thread and worker clocks appear misaligned; cross-context values may be unreliable." : ""}
        </p>
        {handoffProfile ? (
          <dl>
            <div><dt>Import correlation ID</dt><dd>{handoffProfile.correlationId}</dd></div>
            <div><dt>Worker creation</dt><dd>{formatDurationMs(handoffProfile.durations.workerConstructionMs)}</dd></div>
            <div><dt>READY latency</dt><dd>{formatDurationMs(handoffProfile.durations.workerReadyLatencyMs)}</dd></div>
            <div><dt>Main-thread file read</dt><dd>{formatDurationMs(handoffProfile.durations.fileReadMs)}</dd></div>
            <div><dt>postMessage synchronous duration</dt><dd>{formatDurationMs(handoffProfile.durations.payloadPostMessageSyncMs)}</dd></div>
            <div><dt>Worker receive latency</dt><dd>{formatDurationMs(handoffProfile.durations.payloadDeliveryLatencyMs)}</dd></div>
            <div><dt>Worker processing duration</dt><dd>{formatDurationMs(handoffProfile.durations.workerProcessingMs)}</dd></div>
            <div><dt>Worker result-return latency</dt><dd>{formatDurationMs(handoffProfile.durations.workerResultDeliveryLatencyMs)}</dd></div>
            <div><dt>Persistence duration</dt><dd>{formatDurationMs(handoffProfile.durations.persistenceMs)}</dd></div>
            <div><dt>Total duration</dt><dd>{formatDurationMs(handoffProfile.durations.totalMs)}</dd></div>
            <div><dt>Bottleneck classification</dt><dd>{`Bottleneck classification: ${handoffProfile.bottleneck}`}{handoffProfile.bottleneckDetail ? ` — ${handoffProfile.bottleneckDetail}` : ""}</dd></div>
          </dl>
        ) : (
          <p className="command-note">No handoff profile captured yet. Run a historical import to populate this section.</p>
        )}
      </details>

      <p className="command-note" style={{ marginTop: 10 }}>
        Historical imports are transient browser jobs. If the page reloads or the browser suspends, the active import is discarded and must be restarted.
      </p>

      {rows.length > 0 && summary.importsCompleted === 0 && summary.filesRejected === rows.length ? (
        <p className="command-note">All selected files were unsupported. Use FIT or FIT.gz ride exports.</p>
      ) : null}

      {rows.length > 0 && summary.importsCompleted === 0 && summary.duplicatesDetected > 0 && summary.importsFailed === 0 ? (
        <p className="command-note">Duplicate-only import: no new rides were created.</p>
      ) : null}
    </section>
  );
}
